import { defineStore } from "pinia";
import {
  AnyLocation,
  ERiskSummaryType,
  IDisplayItem,
  IDisplaySection,
  IJbaScoreCount,
  ILocationDataLookup,
  IRiskAccuracy,
  IRiskLookup,
  IRiskRecord,
  RiskChartResult,
  RiskLookupType,
  RiskRecord,
  RiskScoreViewer,
  EnergyRatingReport,
  BuildingCountResult,
} from "@/types";
import {
  getRiskIcon,
  getRisksRequestV2,
  getLocationDataRequestV2,
} from "@/util/helpers";
import { get, pick } from "lodash-es";
import { useAxios } from "@/config";
import { useOrgSettings, useApi } from "@/services";
import { formatDataTemplate } from "@gamma/string-pipe";

interface GammaWebsServicesState {
  riskRecords: RiskRecord[];
  addressLinkData: IDisplaySection[];
  isLoadingRisks: boolean;
  riskChartData?: RiskChartResult;
  isRiskChartDataLoading: boolean;
  selectedOtherRiskName?: string;
  selectedRiskName?: string;
}
export const useGammaWebServices = defineStore("gammaWebServices", {
  state: (): GammaWebsServicesState => ({
    riskRecords: [],
    addressLinkData: [],
    isLoadingRisks: false,
    riskChartData: undefined,
    isRiskChartDataLoading: false,
    selectedOtherRiskName: undefined,
    selectedRiskName: undefined,
  }),
  actions: {
    async fetchLocationEnergyRating(
      location: AnyLocation,
      url: string,
      key: string
    ): Promise<EnergyRatingReport | undefined> {
      const request =
        location.type === "Address"
          ? location.addressLinkRequest
          : {
              Coordinates: location.geometry.coordinates,
            };

      const result = await useAxios().$http.post<EnergyRatingReport>(
        url,
        request,
        {
          headers: {
            "x-gammaws-key": key,
            "Content-Type": "application/json",
          },
        }
      );

      return result.data;
    },
    async fetchRiskLookups(
      location: AnyLocation,
      riskLookups: IRiskLookup[]
    ): Promise<Record<string, IRiskRecord>> {
      this.isLoadingRisks = true;

      return Promise.all(
        riskLookups
          .filter((x) => x.type !== RiskLookupType.FloodClimateChangeChart)
          .map((l: IRiskLookup) =>
            useAxios()
              .$http.post(l.url, this.requestBody(location, l), {
                headers: {
                  "x-gammaws-key": l.licenceKey,
                  "Content-Type": "application/json",
                },
              })
              .then(({ data }) => ({
                [l.name]: { ...data, moreInfo: l.moreInfo },
              }))
              .catch(() => Promise.resolve({}))
          )
      )
        .then((r) => Object.assign({}, ...r) as Record<string, IRiskRecord>)
        .then((r) => {
          if (!Object.keys(r).length) {
            this.isLoadingRisks = false;
            return r;
          }
          this.clearScores();
          Object.entries(r).forEach(([name, val]) => {
            this.riskRecords.push(
              new RiskRecord(val, {
                name,
                type: this.getRiskType(
                  riskLookups.find((x) => x.name === name)?.type
                ),
                icon: getRiskIcon(name),
              })
            );
          });

          const defaultSelectedRisk = this.riskRecords.find(
            (r) =>
              r.type !== ERiskSummaryType.Default &&
              this.mainRisks.some((x) => x.name === r.name)
          );
          this.selectRisk(defaultSelectedRisk?.name);

          this.isLoadingRisks = false;
          return r;
        });
    },
    async loadChartData(location: AnyLocation, riskLookup: IRiskLookup) {
      this.riskChartData = undefined;
      try {
        this.isRiskChartDataLoading = true;
        const result = await useAxios().$http.post<RiskChartResult>(
          riskLookup.url,
          this.requestBody(location, riskLookup),
          {
            headers: {
              "x-gammaws-key": riskLookup.licenceKey,
              "Content-Type": "application/json",
            },
          }
        );
        this.riskChartData = result.data;
      } catch (e) {
        console.error(e);
      } finally {
        this.isRiskChartDataLoading = false;
      }
    },
    getRiskType(type: RiskLookupType | undefined) {
      if (type === RiskLookupType.JBA) {
        return ERiskSummaryType.JBA;
      } else if (type === RiskLookupType.FloodClimateChange) {
        return ERiskSummaryType.FloodClimateChange;
      } else {
        return ERiskSummaryType.Default;
      }
    },
    async fetchAddressLinkData(
      location: AnyLocation,
      locationDataLookups: ILocationDataLookup[]
    ): Promise<IDisplaySection[]> {
      this.addressLinkData = [];
      if (location !== undefined && locationDataLookups.length) {
        const requestBody = getLocationDataRequestV2(location);
        if (!requestBody) {
          return Promise.resolve(this.addressLinkData);
        }
        const items = await Promise.all(
          locationDataLookups.map((l: ILocationDataLookup) =>
            useAxios()
              .$http.post(
                l.url,
                { ...requestBody, ...(l.field ? { Field: l.field } : {}) },
                {
                  headers: {
                    "x-gammaws-key": l.licenceKey,
                    "Content-Type": "application/json",
                  },
                }
              )
              .then(
                (r) =>
                  Object.assign(
                    {},
                    {
                      name: l.name,
                      value: this.formatLocationDataValue(l, r.data),
                    }
                  ) as IDisplayItem
              )
              .catch(
                () =>
                  Object.assign(
                    {},
                    { name: l.name, value: "--" }
                  ) as IDisplayItem
              )
          )
        );
        this.addressLinkData = [
          {
            name: "Location Data",
            type: "Table",
            items: items,
          },
        ];
        return Promise.resolve(this.addressLinkData);
      }
      return Promise.resolve([]);
    },
    async fetchBuildingCountData(
      request: { WKTGeometry: string },
      url: string,
      key: string
    ): Promise<BuildingCountResult | undefined> {
      try {
        const result = await useAxios().$http.post<BuildingCountResult>(
          url,
          request,
          {
            headers: {
              "x-gammaws-key": key,
              "Content-Type": "application/json",
            },
          }
        );
        return result.data;
      } catch (e) {
        console.error(e);
        return undefined;
      }
    },
    logLocation(location: AnyLocation) {
      const additionalData = useOrgSettings().selectedRiskOverride;
      return useAxios().$http.put(useApi().sendLog, {
        data: {
          location: location.addressLinkRequest,
          loanPeriod: additionalData?.riskLookupName,
          riskLookups: this.riskRecords.map((r) => ({
            ...pick(r, ["score", "name"]),
            ragScore: r.ragScore?.Level || null,
            scenario: additionalData?.riskLookupValue,
          })),
        },
        action: "locationSearch",
      });
    },
    clearScores() {
      this.riskRecords = [];
    },
    requestBody(location: AnyLocation, l: IRiskLookup): any {
      const loc = getRisksRequestV2(location, l.coordinatesOnly);
      const request =
        l.type === RiskLookupType.JBA ||
        l.type === RiskLookupType.FloodClimateChange ||
        l.type === RiskLookupType.FloodClimateChangeChart
          ? {
              Location: loc,
              Profile: l.profile,
              BufferSize: l.buffer,
              BufferType: l.bufferType,
            }
          : loc;

      const riskOverride = useOrgSettings().selectedRiskOverride;
      if (riskOverride && l.type !== RiskLookupType.FloodClimateChangeChart) {
        request.Scenario = riskOverride.riskLookupValue;
      }

      if (l.type === RiskLookupType.FloodClimateChangeChart)
        request.Scenarios = l.scenarios;

      return request;
    },
    formatLocationDataValue(l: ILocationDataLookup, data: any): any {
      const value = l.property !== undefined ? get(data, l.property) : data;

      if (value == null || value === "") {
        return "--";
      }

      if (l.transform) {
        return formatDataTemplate(l.transform, value);
      }

      return `${value}`;
    },

    selectOtherRisk(name?: string) {
      this.selectedOtherRiskName = name;
    },

    selectRisk(name?: string) {
      const risk = this.riskRecords.find((x) => x.name === name);
      if (!risk) return;

      const lookUps = useOrgSettings().riskLookups;

      if (
        lookUps.find((x) => x.name === name)?.displayLocation === "BottomDrawer"
      ) {
        this.selectedOtherRiskName = name;
      } else {
        this.selectedRiskName = name;
      }
    },
  },
  getters: {
    selectedOtherRisk(): RiskRecord | undefined {
      return (this.riskRecords as RiskRecord[]).find(
        (x) => x.name === this.selectedOtherRiskName
      );
    },
    selectedRisk(): RiskRecord | undefined {
      return (this.riskRecords as RiskRecord[]).find(
        (x) => x.name === this.selectedRiskName
      );
    },
    allRisks(): RiskScoreViewer[] {
      return this.riskRecords.map((record) => ({
        name: record.name,
        score: record.score,
        accuracy: record.Accuracy?.Description || "",
        ragScore: record.ragScore,
        icon: record.icon,
        html: record.html,
        type: record.type,
      }));
    },
    mainRisks(): RiskScoreViewer[] {
      const lookUps = useOrgSettings().riskLookups;
      if (lookUps.findIndex((r) => r.displayLocation === "BottomDrawer") > -1) {
        return this.allRisks.filter(
          (entry) =>
            lookUps.find((r) => r.name === entry.name)?.displayLocation !==
            "BottomDrawer"
        );
      } else {
        return this.allRisks;
      }
    },
    otherRisks(): RiskScoreViewer[] {
      return this.allRisks.filter(
        (risk) => this.mainRisks.findIndex((r) => r.name === risk.name) === -1
      );
    },
    jbaScoreCounts(): IJbaScoreCount[] {
      return this.riskRecords.find((r) => +r.type)?.scoreCounts || [];
    },
    jbaAccuracy(): IRiskAccuracy | undefined {
      return this.riskRecords.find((r) => +r.type)?.Accuracy;
    },
  },
});
